import { useState } from 'react';
import { CreditCardIcon } from 'lucide-react';
import { useAccount, useAskSarahActions, useSignedInAccount } from '../../contexts/AccountContext';
import { founder } from '../../data/content';
import { ApiError, retryPayment } from '../../utils/api';
import { errorMessage } from '../../core/errors';
import { dayShort, daysUntil } from '../../core/format/dates';
import { btnPrimary } from '../../ui/styles';
import { Spinner } from '../../ui/Spinner';

/**
 * Bovenaan Mijn omgeving als een automatische abonnementsbetaling niet lukte (LARAVEL.md F20).
 * Opnieuw betalen gebruikt dezelfde route als de terugkeerpagina na afrekenen: een nieuwe
 * Mollie-link voor dezelfde order, en daarna terug op /bestelling/{orderId}.
 */
export function PaymentIssueBanner() {
  const {
    account
  } = useSignedInAccount();
  const {
    refresh
  } = useAccount();
  const {
    openAsk
  } = useAskSarahActions();
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<{
    tone: 'error' | 'ok';
    text: string;
  } | null>(null);
  const sub = account.subscription;
  // Alleen een lopend abonnement heeft een betaling die nog moet; pauze en stop vervallen hem al (core/rules/subscription.ts).
  const issue = sub?.status === 'actief' ? sub.paymentIssue : undefined;
  if (!issue) return null;
  const date = dayShort(issue.deliveryDate);
  const late = daysUntil(issue.deliveryDate) < 0;
  const pay = async () => {
    if (busy) return;
    setBusy(true);
    setMessage(null);
    try {
      const {
        paymentUrl
      } = await retryPayment(issue.orderId, issue.token);
      // Blijft 'busy': de pagina gaat weg naar Mollie.
      window.location.assign(paymentUrl);
    } catch (err) {
      setBusy(false);
      // Intussen toch betaald (trage webhook): het account opnieuw ophalen, dan verdwijnt de melding.
      if (err instanceof ApiError && err.code === 'already_paid') {
        setMessage({
          tone: 'ok',
          text: 'Hij is intussen al betaald. Je levering gaat gewoon mee.'
        });
        void refresh();
        return;
      }
      setMessage({
        tone: 'error',
        text: errorMessage(err) ?? 'Opnieuw betalen lukte niet. Probeer het zo nog eens.'
      });
    }
  };
  return <section aria-labelledby="payment-issue-title" className="mb-8 flex gap-4 bg-shell p-5 ring-1 ring-clay/40 sm:p-6" data-id="element-6428" data-mp="zeupmwu89oe4">
      <CreditCardIcon className="mt-0.5 h-5 w-5 shrink-0 text-clay" strokeWidth={1.5} aria-hidden="true" data-id="element-6429" data-mp="196r077di36z8" />
      <div className="min-w-0" data-id="element-6430" data-mp="cgrh4izq4q2e">
        <h2 id="payment-issue-title" className="text-[15px] font-medium text-ink" data-id="element-6431" data-mp="chjd3ux8gpt7">
          {issue.reason === 'verlopen' ? `De betaling voor je levering van ${date} is verlopen` : `Je betaling voor de levering van ${date} lukte niet`}
        </h2>
        <p className="mt-1 text-[14px] leading-relaxed text-muted" data-id="element-6432" data-mp="ljiuioc5tvwo">
          Er is niets afgeschreven.{' '}
          {late ? 'Je levering wacht tot de betaling binnen is, daarna gaat hij dezelfde werkdag mee.' : 'Betaal je vóór die dag, dan komt je levering gewoon op tijd.'}{' '}
          Bij het betalen kun je ook een andere betaalmethode kiezen.
        </p>
        {message && <p role={message.tone === 'error' ? 'alert' : 'status'} className={`mt-2 text-[14px] ${message.tone === 'error' ? 'text-clay' : 'text-sage'}`} data-id="element-6433" data-mp="3hedtq3627b4t">
            {message.text}
          </p>}
        <div className="mt-4 flex flex-wrap items-center gap-x-6 gap-y-2 text-[14px]" data-id="element-6434" data-mp="37ou50gvo0n21">
          <button type="button" className={btnPrimary} onClick={() => void pay()} disabled={busy} data-id="element-6435" data-mp="33mz5nhxaojqw">
            {busy && <Spinner data-id="element-6436" data-mp="2yo5s5pi0csqq" />}
            {busy ? 'Even geduld…' : 'Opnieuw betalen'}
          </button>
          <button type="button" onClick={() => openAsk('anders')} aria-haspopup="dialog" className="py-2 text-muted underline-offset-4 transition-colors duration-150 ease-soft hover:text-ink hover:underline" data-id="element-6437" data-mp="2tojdrcqg16gc">
            Lukt het niet? Vraag {founder.firstName}
          </button>
        </div>
      </div>
    </section>;
}